import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";

import reportWebVitals from "./reportWebVitals";
import theme from "./Common/style/core/config/theme";
import persistStore from "redux-persist/es/persistStore";
import store from "./store/store";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { StoreProvider as EProvider } from "easy-peasy";
import { createStore } from "easy-peasy";
import models from "./store";
import { TeamContextProvider } from "./Common/Contex/TeamContext";

/*
 * Recovers a tab that was open when a new build shipped.
 *
 * Route chunks are content-hashed, so a deploy replaces every filename. A page
 * already running keeps asking for the names it was built with, those files are
 * gone, and the SPA rewrite answers each one with index.html: the browser then
 * refuses it for having a MIME type of text/html, and the route never loads.
 * What the user sees is a screen that stops working for no visible reason.
 *
 * The page it needs is the one already being served, so fetching it again is
 * the whole fix.
 *
 * Both guards below are load-bearing, and the first version of this had
 * neither: it reloaded on any failed import, cleared its own guard on every
 * load, and so turned one unlucky request into a page that reloaded on every
 * navigation.
 */
const RELOADED_FOR_STALE_CHUNK = "htptv:reloaded-for-stale-chunk";

window.addEventListener("vite:preloadError", (event) => {
  /*
   * Only this app's own chunks. The event also fires for the Audience Layer
   * remote, which is deployed separately and may be redeploying, offline, or
   * simply slow; reloading this page for that would let a widget beside the
   * video restart the page the fan is watching it on. The panel's error
   * boundary already contains a remote that will not load.
   */
  // Vite puts the error on `payload` (a plain Event, not a CustomEvent), and
  // its message carries the URL that failed.
  const failed = String((event as Event & { payload?: unknown }).payload ?? "");
  if (!failed.includes(window.location.origin)) return;

  /*
   * Once per session, and deliberately not cleared on load. Clearing it there
   * meant every reload handed the guard straight back, so a failure that
   * survived the reload could reload again, and again. One retry either fixes
   * it or proves it is not staleness.
   */
  if (sessionStorage.getItem(RELOADED_FOR_STALE_CHUNK)) return;
  event.preventDefault();
  sessionStorage.setItem(RELOADED_FOR_STALE_CHUNK, "1");
  window.location.reload();
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
const Estore = createStore(models);
const persistor = persistStore(store);
root.render(
  <Provider store={store}>
    <EProvider store={Estore}>
      <PersistGate persistor={persistor} loading={<div>Loading...</div>}>
        <TeamContextProvider>
          <App />
        </TeamContextProvider>
      </PersistGate>
    </EProvider>
  </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
